import type {ThemeCustomization} from "@project/design-system-core";

import {THEME_REGISTRY} from "@project/design-system-core";

const STYLE_ELEMENT_ID = "@project/theme-overrides";

/**
 * Converts an array of ThemeCustomization objects into a CSS string of
 * scoped custom-property declarations.
 *
 * Selector strategy (per HeroUI's DOM structure):
 *  - Built-in light → `:root, [data-theme="light"]  { … }`
 *  - Built-in dark  → `.dark, [data-theme="dark"]   { … }`
 *  - Custom theme   → `[data-theme="ocean"]          { color-scheme: light; … }`
 *
 * Entries with zero token overrides are skipped entirely.
 * Returns an empty string when there is nothing to inject.
 */
export function generateThemeCSS(customizations: ThemeCustomization[]): string {
  const blocks: string[] = [];

  for (const {themeId, tokens} of customizations) {
    const entries = Object.entries(tokens) as [string, string][];

    if (entries.length === 0) continue;

    const config = THEME_REGISTRY[themeId];

    if (!config) continue;

    let selector: string;
    const lines: string[] = [];

    if (config.builtin) {
      // Built-in themes follow HeroUI's own selector conventions
      selector =
        config.colorScheme === "light"
          ? ':root, [data-theme="light"]'
          : `.dark, [data-theme="dark"]`;
    } else {
      // Custom themes use only the data-theme attribute selector
      selector = `[data-theme="${themeId}"]`;
      // Explicitly declare color-scheme so the browser applies the right
      // system-UI chrome (scrollbars, form controls, etc.)
      lines.push(`  color-scheme: ${config.colorScheme};`);
    }

    for (const [key, value] of entries) {
      lines.push(`  --${key}: ${value};`);
    }

    blocks.push(`${selector} {\n${lines.join("\n")}\n}`);
  }

  return blocks.join("\n\n");
}

/**
 * Injects (or replaces) a <style> element in <head> with the given CSS.
 * Idempotent: always targets the element with id STYLE_ELEMENT_ID.
 * If css is an empty string, removes any previously injected element.
 */
export function injectThemeStyles(css: string): void {
  const existing = document.getElementById(STYLE_ELEMENT_ID);

  if (!css) {
    existing?.remove();

    return;
  }

  const el = existing ?? document.createElement("style");

  el.id = STYLE_ELEMENT_ID;
  el.textContent = css;

  if (!existing) {
    document.head.appendChild(el);
  }
}

/* -------------------------------------------------------------------------------------------------
 * Theme Builder Preview Styles
 * Isolated from main theme overrides via a separate style element ID.
 * -----------------------------------------------------------------------------------------------*/

const BUILDER_STYLE_ID = "@project/theme-builder-preview";

/**
 * Injects (or replaces) the builder preview <style> element in <head>.
 * Completely isolated from the main theme overrides style element.
 * If css is an empty string, removes any previously injected element.
 */
export function injectBuilderStyles(css: string): void {
  const existing = document.getElementById(BUILDER_STYLE_ID);

  if (!css) {
    existing?.remove();

    return;
  }

  const el = existing ?? document.createElement("style");

  el.id = BUILDER_STYLE_ID;
  el.textContent = css;

  if (!existing) {
    document.head.appendChild(el);
  }
}

/**
 * Removes the builder preview style element from <head>.
 * Reverts any live preview applied by the theme builder.
 */
export function removeBuilderStyles(): void {
  injectBuilderStyles("");
}
